import React from "react";
import { GlobalContext } from "./GlobalContext";
import { TranslateContext } from "./TranslateContext";
import { pollVideoJob } from "../user/videoStudio/api/generateVideo";
import {
  deleteClippingJob,
  fetchClippingResult,
  fetchClippingStatus,
} from "../user/videoStudio/clipping/api/clippingApi";
import { jobKey, loadBackgroundJobs, saveBackgroundJobs } from "./backgroundJobsStorage";

const CLIPPING_POLL_MS = 10_000;
const VIDEO_POLL_MS = 5_000;
const CLIPPING_TIMEOUT_MS = 45 * 60 * 1000;
const VIDEO_TIMEOUT_MS = 45 * 60 * 1000;

const BackgroundJobsContext = React.createContext(null);

function isActiveJob(job) {
  return job && ["submitting", "polling", "fetching", "generating", "queued"].includes(job.phase);
}

export function BackgroundJobsProvider({ children }) {
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const [jobs, setJobs] = React.useState(() => loadBackgroundJobs());
  const jobsRef = React.useRef(jobs);
  const pollingRef = React.useRef(false);
  const listenersRef = React.useRef(new Map());

  React.useEffect(() => {
    jobsRef.current = jobs;
    saveBackgroundJobs(jobs.filter((j) => j.phase !== "dismissed"));
  }, [jobs]);

  const emit = React.useCallback((key, job) => {
    const set = listenersRef.current.get(key);
    if (set) set.forEach((fn) => fn(job));
  }, []);

  const patchJob = React.useCallback((key, patch) => {
    setJobs((prev) => {
      const idx = prev.findIndex((j) => j.key === key);
      if (idx === -1) return prev;
      const next = [...prev];
      const prevJob = next[idx];
      next[idx] = { ...prevJob, ...patch };
      const updated = next[idx];
      queueMicrotask(() => {
        emit(key, updated);
        if (
          patch.phase &&
          patch.phase !== prevJob.phase &&
          (patch.phase === "complete" || patch.phase === "failed")
        ) {
          window.dispatchEvent(
            new CustomEvent("beyundai:background-job", { detail: { job: updated } }),
          );
        }
      });
      return next;
    });
  }, [emit]);

  const removeJob = React.useCallback((key) => {
    setJobs((prev) => prev.filter((j) => j.key !== key));
    listenersRef.current.delete(key);
  }, []);

  const notifyOnce = React.useCallback(
    (key, message, success) => {
      const job = jobsRef.current.find((j) => j.key === key);
      if (!job || job.notified) return;
      patchJob(key, { notified: true });
      showSnack?.(message, success);
    },
    [patchJob, showSnack],
  );

  const pollClipping = React.useCallback(
    async (job) => {
      const id = job.externalId;
      if (Date.now() - job.startedAt > CLIPPING_TIMEOUT_MS) {
        patchJob(job.key, {
          phase: "failed",
          error: lang?.backgroundJobClippingTimeout || "Clipping timed out after 45 minutes.",
        });
        notifyOnce(
          job.key,
          lang?.backgroundJobClippingTimeout || "Clipping timed out after 45 minutes.",
          false,
        );
        return;
      }

      const data = await fetchClippingStatus(hitAxios, id);
      patchJob(job.key, { status: data, phase: "polling" });

      if (data.status === "completed") {
        const progress = Number(data.progress);
        if (!Number.isFinite(progress) || progress < 100) return;

        patchJob(job.key, { phase: "fetching" });
        const resultId = data._id || id;
        try {
          const result = await fetchClippingResult(hitAxios, resultId);
          patchJob(job.key, { phase: "complete", result, status: data });
          notifyOnce(
            job.key,
            lang?.backgroundJobClippingDone || "Your clips are ready — open AI Clipping → My clips.",
            true,
          );
        } catch (err) {
          try {
            const recheck = await fetchClippingStatus(hitAxios, id);
            if (recheck.status === "failed") {
              const msg = recheck.failureReason || "Processing failed.";
              patchJob(job.key, { phase: "failed", status: recheck, error: msg });
              notifyOnce(job.key, msg, false);
              return;
            }
          } catch {
            /* fall through */
          }
          const msg = err?.message || "Could not fetch generated clips.";
          patchJob(job.key, { phase: "failed", error: msg });
          notifyOnce(job.key, msg, false);
        }
        return;
      }

      if (data.status === "failed") {
        const msg = data.failureReason || "Processing failed.";
        patchJob(job.key, { phase: "failed", status: data, error: msg });
        notifyOnce(job.key, msg, false);
        return;
      }

      if (data.status === "cancelled") {
        patchJob(job.key, { phase: "failed", error: "Request was cancelled." });
        notifyOnce(job.key, "Clipping job was cancelled.", false);
      }
    },
    [hitAxios, lang, notifyOnce, patchJob],
  );

  const pollVideo = React.useCallback(
    async (job) => {
      if (Date.now() - job.startedAt > VIDEO_TIMEOUT_MS) {
        const msg =
          lang?.backgroundJobVideoTimeout ||
          "Video generation is taking longer than expected — check Video Library.";
        patchJob(job.key, { phase: "failed", error: msg });
        notifyOnce(job.key, msg, false);
        return;
      }

      const row = await pollVideoJob(hitAxios, Number(job.externalId));
      const done =
        row.status === "completed" || row.status === "segment_ready";
      const failed = row.status === "failed";
      patchJob(job.key, {
        status: row.status,
        videoUrl: row.video_url || null,
        error: row.error_message || null,
        phase: done ? "complete" : failed ? "failed" : "generating",
      });

      if (done && row.video_url) {
        notifyOnce(
          job.key,
          lang?.backgroundJobVideoDone || "Your video is ready — open Video Library to view it.",
          true,
        );
        return;
      }

      if (done && !row.video_url) {
        // Completed without URL is rare; stop spinning the UI anyway.
        notifyOnce(
          job.key,
          lang?.backgroundJobVideoDoneNoUrl ||
            "Video finished processing — open Video Library to refresh.",
          true,
        );
        return;
      }

      if (failed) {
        notifyOnce(job.key, row.error_message || "Video generation failed.", false);
      }
    },
    [hitAxios, lang, notifyOnce, patchJob],
  );

  const runPollCycle = React.useCallback(async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    try {
      const now = Date.now();
      const active = jobsRef.current.filter(isActiveJob);
      for (const job of active) {
        const lastPoll = job.lastPollAt || 0;
        const minGap = job.type === "clipping" ? CLIPPING_POLL_MS : VIDEO_POLL_MS;
        if (now - lastPoll < minGap) continue;
        patchJob(job.key, { lastPollAt: now });
        try {
          if (job.type === "clipping") await pollClipping(job);
          else if (job.type === "video-studio") await pollVideo(job);
        } catch (err) {
          const msg = err?.message || "Background poll failed.";
          patchJob(job.key, { phase: "failed", error: msg });
          notifyOnce(job.key, msg, false);
        }
      }
    } finally {
      pollingRef.current = false;
    }
  }, [notifyOnce, patchJob, pollClipping, pollVideo]);

  React.useEffect(() => {
    const tick = () => {
      const active = jobsRef.current.filter(isActiveJob);
      if (!active.length) return;
      runPollCycle();
    };
    tick();
    const timer = setInterval(tick, 4000);
    return () => clearInterval(timer);
  }, [runPollCycle]);

  const registerClippingJob = React.useCallback(
    (requestId, createMeta = null) => {
      const key = jobKey("clipping", requestId);
      const entry = {
        key,
        type: "clipping",
        externalId: String(requestId),
        phase: "polling",
        startedAt: Date.now(),
        lastPollAt: 0,
        status: null,
        result: null,
        error: null,
        createMeta,
        notified: false,
      };
      setJobs((prev) => {
        const filtered = prev.filter((j) => j.key !== key);
        return [...filtered, entry];
      });
      queueMicrotask(() => emit(key, entry));
      return key;
    },
    [emit],
  );

  const registerVideoJob = React.useCallback(
    (jobId, meta = {}) => {
      const key = jobKey("video-studio", jobId);
      const entry = {
        key,
        type: "video-studio",
        externalId: String(jobId),
        phase: "generating",
        startedAt: Date.now(),
        lastPollAt: 0,
        status: "generating",
        videoUrl: null,
        error: null,
        title: meta.title || "",
        categoryId: meta.categoryId || "",
        notified: false,
      };
      setJobs((prev) => {
        const filtered = prev.filter((j) => j.key !== key);
        return [...filtered, entry];
      });
      queueMicrotask(() => emit(key, entry));
      return key;
    },
    [emit],
  );

  const subscribe = React.useCallback((key, listener) => {
    if (!key) return () => {};
    if (!listenersRef.current.has(key)) listenersRef.current.set(key, new Set());
    listenersRef.current.get(key).add(listener);
    const current = jobsRef.current.find((j) => j.key === key);
    if (current) listener(current);
    return () => listenersRef.current.get(key)?.delete(listener);
  }, []);

  const getJob = React.useCallback((key) => jobsRef.current.find((j) => j.key === key), []);

  const getActiveJobs = React.useCallback(
    () => jobsRef.current.filter(isActiveJob),
    [],
  );

  const getLatestJobByType = React.useCallback((type) => {
    const list = jobsRef.current.filter((j) => j.type === type);
    return list.length ? list[list.length - 1] : null;
  }, []);

  const dismissJob = React.useCallback((key) => {
    removeJob(key);
  }, [removeJob]);

  const cancelClippingJob = React.useCallback(
    async (key) => {
      const job = jobsRef.current.find((j) => j.key === key);
      if (!job) return;
      try {
        await deleteClippingJob(hitAxios, job.externalId);
      } catch {
        /* still remove locally */
      }
      removeJob(key);
    },
    [hitAxios, removeJob],
  );

  const value = React.useMemo(
    () => ({
      jobs,
      activeCount: jobs.filter(isActiveJob).length,
      registerClippingJob,
      registerVideoJob,
      subscribe,
      getJob,
      getActiveJobs,
      getLatestJobByType,
      dismissJob,
      cancelClippingJob,
      CLIPPING_POLL_MS,
      VIDEO_POLL_MS,
    }),
    [
      jobs,
      registerClippingJob,
      registerVideoJob,
      subscribe,
      getJob,
      getActiveJobs,
      getLatestJobByType,
      dismissJob,
      cancelClippingJob,
    ],
  );

  return (
    <BackgroundJobsContext.Provider value={value}>{children}</BackgroundJobsContext.Provider>
  );
}

export function useBackgroundJobs() {
  const ctx = React.useContext(BackgroundJobsContext);
  if (!ctx) {
    throw new Error("useBackgroundJobs must be used within BackgroundJobsProvider");
  }
  return ctx;
}

export function useBackgroundJob(key) {
  const { subscribe, getJob } = useBackgroundJobs();
  const [job, setJob] = React.useState(() => (key ? getJob(key) : null));

  React.useEffect(() => {
    if (!key) {
      setJob(null);
      return undefined;
    }
    return subscribe(key, setJob);
  }, [key, subscribe]);

  return job;
}
